import React from 'react';
import PropTypes from 'prop-types';
import { Box, Text, TextArea } from 'grommet';
import { Add, FormClose } from 'grommet-icons';
import { AppButton } from '@Components/Control';
import { useAppTheme } from '@Components/Context';
import { ProductOfferingType } from '@RedflagAI/node-utility/interface';
import {
  ProductFormSingleTextInput,
  ProductFormCheckBoxInput,
} from '@Components/Partial/Product/NewSearch';

const TYPE_LABELS = {
  [ProductOfferingType.CID]: 'YouTube Content ID',
  [ProductOfferingType.SOCIAL]: 'Social Platform Monitoring',
  [ProductOfferingType.WATERMARKING]: 'Watermarking',
  [ProductOfferingType.CUSTOM]: 'Custom Product',
};

const TYPE_DESCRIPTIONS = {
  [ProductOfferingType.CID]: 'YouTube Content ID management and revenue sharing.',
  [ProductOfferingType.SOCIAL]: 'Multi-platform social media monitoring and enforcement.',
  [ProductOfferingType.WATERMARKING]: 'Digital watermarking for piracy detection.',
};

export const makeDefaultConfig = (productType) => {
  const isCid = productType === ProductOfferingType.CID;
  const isSocial = productType === ProductOfferingType.SOCIAL;

  let customPlanData;
  if (isCid) {
    customPlanData = {
      uploader: 'redflag',
      setupFeeAmount: '',
      monthlyFeeAmount: '',
      revenueSharePercent: '',
      revenueShareStartMonth: '',
    };
  } else if (isSocial) {
    customPlanData = { defaultPerPlatformAmount: 10, platformOverrides: [] };
  } else {
    customPlanData = { setupFee: '', frequency: 'monthly', amount: '' };
  }

  let config;
  if (isCid) {
    config = { uploader: 'redflag', channels: [''] };
  } else if (isSocial) {
    config = { platforms: [], mainPlatform: '', mainLink: '' };
  } else {
    config = {};
  }

  return {
    planMode: (isSocial || isCid) ? 'custom' : 'existing',
    planId: '',
    planLabel: '',
    customPlan: {
      name: '',
      description: '',
      data: customPlanData,
    },
    config,
  };
};

const KNOWN_TYPES = [
  ProductOfferingType.CID,
  ProductOfferingType.SOCIAL,
  ProductOfferingType.WATERMARKING,
];

const Step2ProductSelection = ({
  small,
  pricingData = null,
  selectedProducts,
  onProductAdd,
  onProductRemove,
}) => {
  const { theme } = useAppTheme();
  const { page, button } = theme;
  const [showCustomForm, setShowCustomForm] = React.useState(false);
  const [customTitle, setCustomTitle] = React.useState('');
  const [customDescription, setCustomDescription] = React.useState('');

  const catalogProducts = pricingData?.catalog?.products || [];

  const selectedIds = new Set(
    selectedProducts.filter((p) => p.productId).map((p) => p.productId),
  );
  const selectedKnownTypes = new Set(
    selectedProducts
      .filter((p) => p.mode === 'existing' && !p.productId)
      .map((p) => p.productType),
  );

  const isKnownTypeSelected = (type) => {
    const catalogMatch = catalogProducts.find((p) => p.type === type);
    if (catalogMatch) return selectedIds.has(catalogMatch.id);
    return selectedKnownTypes.has(type);
  };

  const handleToggleKnownType = (type) => {
    if (isKnownTypeSelected(type)) {
      const idx = selectedProducts.findIndex(
        (p) => p.productType === type && p.mode === 'existing',
      );
      if (idx !== -1) onProductRemove(idx);
      return;
    }
    const catalogMatch = catalogProducts.find((p) => p.type === type);
    const newProduct = catalogMatch
      ? {
        mode: 'existing',
        productId: catalogMatch.id,
        productKey: catalogMatch.key || catalogMatch.productKey || null,
        productLabel: catalogMatch.title || TYPE_LABELS[type],
        productType: type,
        catalogPlans: catalogMatch.plans || [],
      }
      : {
        mode: 'existing',
        productId: null,
        productKey: null,
        productLabel: TYPE_LABELS[type],
        productType: type,
        catalogPlans: [],
      };
    onProductAdd(newProduct, makeDefaultConfig(type));
  };

  const handleAddCustom = () => {
    if (!customTitle.trim()) return;
    const newProduct = {
      mode: 'new',
      productType: ProductOfferingType.CUSTOM,
      productLabel: customTitle.trim(),
      newProduct: { title: customTitle.trim(), description: customDescription.trim() },
      catalogPlans: [],
    };
    onProductAdd(newProduct, {
      ...makeDefaultConfig(ProductOfferingType.CUSTOM),
      planMode: 'custom',
    });
    setCustomTitle('');
    setCustomDescription('');
    setShowCustomForm(false);
  };

  return (
    <Box gap="1.25rem">
      <Text size="0.85rem" color={page.secondaryText}>
        Select the products to include in this onboarding. At least one is required.
      </Text>
      <Box gap="0.75rem">
        {KNOWN_TYPES.map((type) => {
          const selected = isKnownTypeSelected(type);
          return (
            <Box
              key={type}
              direction="row"
              align="center"
              justify="between"
              pad="1rem"
              round="var(--app-radius)"
              border={{ color: selected ? button.primaryBg : page.border, size: '1px' }}
              background={selected ? `${button.primaryBg}18` : page.altBg}
              style={{ cursor: 'pointer' }}
              onClick={() => handleToggleKnownType(type)}
            >
              <Box direction="row" align="center" gap="0.75rem">
                <Box onClick={(e) => e.stopPropagation()}>
                  <ProductFormCheckBoxInput
                    controlled
                    value={selected}
                    handleFormValues={() => handleToggleKnownType(type)}
                    borderColor={page.border}
                    focusHighlight={page.focusHighlight}
                    checkmarkColor={button.primaryBg}
                  />
                </Box>
                <Box>
                  <Text size="0.9rem" weight={600} color={page.primaryText}>
                    {TYPE_LABELS[type]}
                  </Text>
                  {TYPE_DESCRIPTIONS[type] && (
                    <Text size="0.75rem" color={page.secondaryText}>
                      {TYPE_DESCRIPTIONS[type]}
                    </Text>
                  )}
                </Box>
              </Box>
            </Box>
          );
        })}
      </Box>
      {selectedProducts.filter((p) => p.mode === 'new').map((p) => {
        const realIdx = selectedProducts.indexOf(p);
        return (
          <Box
            key={p.productLabel}
            direction="row"
            align="center"
            justify="between"
            pad="1rem"
            round="var(--app-radius)"
            border={{ color: button.primaryBg, size: '1px' }}
            background={`${button.primaryBg}18`}
          >
            <Box gap="0.2rem">
              <Text size="0.9rem" weight={600} color={page.primaryText}>
                {p.productLabel}
              </Text>
              <Text size="0.75rem" color={page.secondaryText}>(custom)</Text>
              {p.newProduct?.description && (
                <Text size="0.75rem" color={page.secondaryText}>
                  {p.newProduct.description}
                </Text>
              )}
            </Box>
            <AppButton
              level="dynamicSmall"
              overrideHover
              bgColor="var(--app-error)"
              color="white"
              label={(
                <Box direction="row" align="center" gap="0.4rem">
                  <Text size="0.875rem">Remove</Text>
                  <FormClose size="1rem" />
                </Box>
              )}
              onClick={() => onProductRemove(realIdx)}
            />
          </Box>
        );
      })}
      {!showCustomForm ? (
        <AppButton
          overrideHover
          extraWide
          level="dynamicSmall"
          bgColor={page.altBg}
          color={page.primaryText}
          borderColor={button.border}
          style={{ border: `1px dashed ${button.border}`, alignSelf: 'flex-start' }}
          label={(
            <Box direction="row" align="center" gap="0.4rem">
              <Add size="1rem" />
              <Text size="0.875rem">Add custom product</Text>
            </Box>
          )}
          onClick={() => setShowCustomForm(true)}
        />
      ) : (
        <Box
          pad="1rem"
          gap="0.75rem"
          round="var(--app-radius)"
          border={{ color: button.border, size: '1px' }}
          background={page.altBg}
        >
          <Text size="0.85rem" weight={600} color={page.primaryText}>New Custom Product</Text>
          <Box gap="0.25rem">
            <Box direction="row" gap="0.2rem">
              <Text size="0.8rem" color={page.primaryText}>Title</Text>
              <Text size="0.8rem" color="var(--app-error)">*</Text>
            </Box>
            <ProductFormSingleTextInput
              small={small}
              focusHighlight={page.focusHighlight}
              value={customTitle}
              handleFormValues={setCustomTitle}
              placeholder="e.g. Custom Licensing Service"
            />
          </Box>
          <Box gap="0.25rem">
            <Text size="0.8rem" color={page.primaryText}>Description (optional)</Text>
            <TextArea
              value={customDescription}
              onChange={(e) => setCustomDescription(e.target.value)}
              placeholder="Brief description of the product"
              rows={2}
            />
          </Box>
          <Box direction="row" gap="0.75rem">
            <AppButton
              overrideHover
              level="dynamicSmall"
              bgColor={button.primaryBg}
              color="white"
              label="Add Product"
              disabled={!customTitle.trim()}
              onClick={handleAddCustom}
            />
            <AppButton
              overrideHover
              level="dynamicSmall"
              bgColor={page.altBg}
              color={page.primaryText}
              borderColor={button.border}
              style={{ border: `1px solid ${button.border}` }}
              label="Cancel"
              onClick={() => {
                setShowCustomForm(false);
                setCustomTitle('');
                setCustomDescription('');
              }}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
};

Step2ProductSelection.propTypes = {
  small: PropTypes.bool.isRequired,
  pricingData: PropTypes.object,
  selectedProducts: PropTypes.array.isRequired,
  onProductAdd: PropTypes.func.isRequired,
  onProductRemove: PropTypes.func.isRequired,
};

export default Step2ProductSelection;
